/* ----------------------------------------------------------------------------
 * useBatchPublishVoice — Aura publish P1 glue for the voice controller (owner
 * decision 10-10-2026). Owns the on-screen batch view + the closed-set voice
 * confirm loop; useVoiceController only routes into it (onFinal / submitText)
 * and renders the view. The server is authoritative for the read-back: on a
 * confirm we PROPOSE, speak the server's spoken_readback ("Publishing 3 posts to
 * booksesh.com."), then EXECUTE exactly that read-back.
 *
 * Voice rules (owner + 1437 safety design; site/action intent fix 10-10-2026):
 *   - the blog is never read out; cards are shown;
 *   - sites come ONLY from the domain being viewed (header "Viewing: <domain>");
 *     a site named outside it is refused with one line, never a silent switch;
 *   - action named in the command ("push"/"publish" = publish, "save as draft" =
 *     draft) + clear site -> ONE read-back naming action, site and count; then
 *     "yes" / "confirm" / "go ahead" / a tap confirms exactly that read-back;
 *   - no action named -> "publish or save as draft?"; there a bare "yes" gets
 *     "Publish or save as draft?"; ambiguity re-asks once then cancels;
 *   - taps on the cards work at any time (channel 'tap');
 *   - no drive/parked flag exists in the app, so none is used (DRIVE_MODE_NOTE).
 * Errors are never swallowed silently: every failure lands in view.error and is
 * spoken briefly (no-silent-catch rule for the turn path).
 * -------------------------------------------------------------------------- */

import { useCallback, useRef, useState, type MutableRefObject } from 'react';
import type { ConversationTurn } from '../wake/turnEntity.ts';
import { voiceBeacon } from '../net/voiceDiag.ts';
import {
  BATCH_ASK_MODE,
  BATCH_MAX_COUNT,
  matchDraftTitle,
  titleNotFoundLine,
  BATCH_CANCEL_ACK,
  BATCH_WRITES_SKIPPED,
  BATCH_REVERTED,
  BATCH_REVERT_ALREADY_DRAFT,
  BATCH_REVERT_FAILED,
  BATCH_NO_SITE,
  BATCH_NONE_READY,
  BATCH_TARGET_UNLINKED_REASON,
  BATCH_UPDATE_LIVE_UNSUPPORTED,
  BATCH_CONNECTOR_UPDATE_REQUIRED,
  BATCH_CONNECTOR_UPDATE_REQUIRED_CODE,
  BATCH_UPDATE_LIVE_WHICH,
  BATCH_UPDATE_LIVE_REASK,
  updateLiveNotFoundLine,
  alreadyLiveLine,
  batchTargetUnlinkedLine,
  BATCH_OUT_OF_DOMAIN,
  BATCH_PICK_SITE,
  batchConfirmReask,
  BATCH_REASK,
  BATCH_OWNER_OPERATOR_ONLY,
  batchProposalLine,
  batchReadbackLine,
  batchSubsetQuestion,
  emptyBatchView,
  forbiddenBatchView,
  isPublishFeatureForbidden,
  matchBatchPublishAnswer,
  mergeBatchResults,
  pendingDraftIds,
  sameUpdateAsk,
  tapUpdateAsk,
  resolveBatchPublishAnswer,
  resolveBatchSite,
  type BatchActiveDomain,
  type BatchChannel,
  type BatchMode,
  type BatchPublishAsk,
  type BatchPublishView,
  type BatchSiteOption,
  type BatchViewCard,
  type BatchLiveUpdateView,
} from './batchPublishModel.ts';
import type { UpdateLiveProposal, UpdateLiveResult } from '@/data/publishBatches';
import { clarifyLine, parseListSelection } from './recommendModel.ts';

export interface BatchPublishDeps {
  listSites(): Promise<BatchSiteOption[]>;
  candidates(targetId: number, limit: number, draftIds?: number[]): Promise<{
    target: { id: number; name: string; label: string; url: string };
    publish: { available: boolean; reason: string; message: string };
    items: BatchViewCard[];
    already_live?: { draft_id: number; title: string }[];
    reason: string;
  }>;
  confirm(
    targetId: number,
    draftIds: number[],
    mode: BatchMode,
    channel: BatchChannel,
    onReadback: (p: { spoken_readback: string; effective_mode: BatchMode; mode_message: string }) => void | Promise<void>,
  ): Promise<{ result: { items: { draft_id: number; status: string; reason: string; publication_id: number | null; undo_available: boolean }[]; spoken_result: string; effective_mode: BatchMode; mode_message: string } }>;
  revert(publicationId: number): Promise<{ ok: boolean; changed: boolean }>;
  /** Connector 1.3.0 update_live (owner GO 12-10-2026 15:53 UTC, option A). Optional:
   *  absent = this build cannot update a live post (the old refusal line is spoken). */
  proposeUpdateLive?(targetId: number, draftId: number, channel: BatchChannel): Promise<UpdateLiveProposal>;
  executeUpdateLive?(proposal: Pick<UpdateLiveProposal, 'confirm_token' | 'target_id' | 'draft_id'>): Promise<UpdateLiveResult>;
  undoUpdateLive?(batchId: number): Promise<{ ok: boolean; changed: boolean; spoken_result: string }>;
}

export interface UseBatchPublishVoiceArgs {
  deps: BatchPublishDeps;
  speak: (text: string) => Promise<void>;
  turnRef: MutableRefObject<ConversationTurn>;
  /** re-opens the answer capture (startListening), indirected via a ref. */
  listenRef: MutableRefObject<(() => Promise<unknown>) | null>;
  /** the domain being viewed (header "Viewing: <domain>"): site options are scoped
   *  to it. Preferred over activeProjectId (kept for legacy callers). */
  activeDomain?: () => BatchActiveDomain | null;
  /** legacy: the active SEO project id only. */
  activeProjectId?: () => number | null;
  /** C6b: may the signed-in human (role in the ACTIVE workspace) use this feature at all?
   *  Owner/operator only (owner decision 10-10-2026). Omitted = NO (fail-closed). The
   *  server enforces the same rule (403 publish_feature_owner_operator_only). */
  canManagePublishing?: () => boolean;
}

export interface BatchPublishVoice {
  view: BatchPublishView | null;
  begin: (ask: BatchPublishAsk, channel: BatchChannel) => Promise<void>;
  handleAnswer: (answer: string, channel: BatchChannel) => Promise<void>;
  confirm: (mode: BatchMode, draftIds?: number[], channel?: BatchChannel) => Promise<void>;
  chooseSite: (siteId: number) => Promise<void>;
  /** Aura P2 hand-off: show exactly these just-written drafts as Draft/Publish cards.
   *  conversational-actions: `mode` = the action already named (the read-back then names
   *  action + site + count); `afterConfirm` runs once after that confirm executed. */
  presentDrafts: (site: BatchSiteOption, draftIds: number[], channel: BatchChannel, opts?: { mode?: BatchMode | null; afterConfirm?: () => Promise<void> }) => Promise<void>;
  undo: (publicationId: number, draftId: number) => Promise<void>;
  dismiss: () => void;
  isAwaitingAnswer: () => boolean;
  /** Fable IVG C2: drop writes queued behind an open publish read-back (Stop / close / new ask). */
  dropQueuedWrites: () => void;
  /** Connector 1.3.0: tap "Update live post" on an "Already live" entry -> server read-back. */
  updateLive: (draftId: number) => Promise<void>;
  /** Tap to confirm the update read-back (the voice path takes ONLY a pure yes). */
  confirmUpdateLive: (channel?: BatchChannel) => Promise<void>;
  cancelUpdateLive: () => Promise<void>;
  undoUpdateLive: () => Promise<void>;
}

function errCode(e: unknown): string {
  return String((e as { body?: { error?: { code?: string } } })?.body?.error?.code ?? (e as { code?: string })?.code ?? '');
}

function errText(e: unknown): string {
  const code = (e as { code?: string; body?: { error?: { code?: string } } })?.body?.error?.code
    ?? (e as { code?: string })?.code;
  return code ? 'That didn’t go through (' + String(code).replace(/_/g, ' ') + ').' : 'That didn’t go through. Please try again.';
}

export function useBatchPublishVoice(args: UseBatchPublishVoiceArgs): BatchPublishVoice {
  const { deps, speak, turnRef, listenRef } = args;
  const [view, setView] = useState<BatchPublishView | null>(null);
  const viewRef = useRef<BatchPublishView | null>(null);
  const pendingCount = useRef<number>(3);
  // the action the user NAMED in the ask (carried through a site choice), or null.
  const pendingMode = useRef<BatchMode | null>(null);
  const pendingTitle = useRef<string | null>(null); // F3: named draft title, if any
  // Connector 1.3.0 update_live: the ask is an update of a LIVE post (title may be null =
  // "update it on the live site"); the last "already live" refusal gives "it" its meaning;
  // the proposal (single-use token) lives only in memory for the one execute.
  const pendingLiveUpdate = useRef<boolean>(false);
  const lastLive = useRef<{ site: { id: number; name: string; label: string }; draft_id: number; title: string } | null>(null);
  const liveProposal = useRef<UpdateLiveProposal | null>(null);
  // conversational-actions: "write <title> that is on the queue" -> "already written" offer.
  const pendingAlreadyWritten = useRef<boolean>(false);
  // conversational-actions: a mixed recommend selection writes AFTER the publish confirm ran.
  const afterConfirmRef = useRef<(() => Promise<void>) | null>(null);
  const set = useCallback((next: BatchPublishView | null) => { viewRef.current = next; setView(next); }, []);
  const patch = useCallback((p: Partial<BatchPublishView>) => {
    if (!viewRef.current) return;
    set({ ...viewRef.current, ...p });
  }, [set]);

  const say = useCallback(async (line: string) => {
    try { await speak(line); } catch (e) {
      // Not swallowed: a TTS failure is surfaced on the card (text is still on screen).
      patch({ error: 'Aura could not speak just now — the options are on screen.' });
      void e;
    }
  }, [speak, patch]);

  // C6b: a role other than owner/operator gets one brief line and nothing actionable
  // (no cards, no buttons, no open voice question). Used up-front AND when the server
  // answers 403 publish_feature_owner_operator_only (e.g. role changed mid-session).
  const refuse = useCallback(async (channel: BatchChannel) => {
    afterConfirmRef.current = null;
    turnRef.current.clearSubStateIfKind('batch_publish');
    set(forbiddenBatchView(channel));
    await say(BATCH_OWNER_OPERATOR_ONLY);
  }, [set, say, turnRef]);

  const loadCandidates = useCallback(async (site: BatchSiteOption, channel: BatchChannel, onlyIds?: number[]) => {
    patch({ phase: 'loading', site: { id: site.id, name: site.name, label: site.label }, sites: [], line: 'Loading ready drafts for ' + site.name + '…' });
    let c;
    try {
      c = onlyIds && onlyIds.length > 0
        ? await deps.candidates(site.id, onlyIds.length, onlyIds)
        : await deps.candidates(site.id, pendingCount.current);
    } catch (e) {
      if (isPublishFeatureForbidden(e)) { await refuse(channel); return; }
      patch({ phase: 'error', error: errText(e), line: 'I couldn’t load the drafts for ' + site.name + '.' });
      await say('I couldn’t load the drafts for ' + site.name + '.');
      return;
    }
    if (!c.items || c.items.length === 0) {
      // A site with no SEO domain linked gets an honest reason, not "no drafts".
      const none = c.reason === BATCH_TARGET_UNLINKED_REASON ? batchTargetUnlinkedLine(c.target.name) : BATCH_NONE_READY;
      patch({ phase: 'none', items: [], line: none });
      await say(none);
      return;
    }
    const alreadyLive = Array.isArray(c.already_live) ? c.already_live : [];
    patch({ alreadyLive });
    const title = pendingTitle.current;
    if (pendingLiveUpdate.current) {
      pendingLiveUpdate.current = false;
      const siteView = { id: c.target.id, name: c.target.name, label: c.target.label };
      const liveHit = title ? matchDraftTitle(title, alreadyLive) : null;
      if (!liveHit) {
        const miss = title ? updateLiveNotFoundLine(title, c.target.name) : BATCH_UPDATE_LIVE_WHICH;
        patch({ phase: 'none', items: [], site: siteView, line: miss });
        await say(miss);
        return;
      }
      patch({ items: [], site: siteView });
      await proposeLive(siteView, liveHit.item, channel);
      return;
    }
    // F13: numbers only -- did candidates arrive, and did a named title match.
    voiceBeacon('batch_candidates', { items: c.items.length, titled: title ? 1 : 0, matched: title ? (matchDraftTitle(title, c.items) ? 1 : 0) : -1, channel });
    if (title) {
      // F3: ONE card -- the ready draft whose title the user named.
      const hit = matchDraftTitle(title, c.items);
      // Re-push guard: the named draft is already LIVE on this site -> never re-pushed.
      const liveHit = !hit ? matchDraftTitle(title, alreadyLive) : null;
      if (liveHit) {
        // remembered so a following "update it on the live site" means THIS post.
        lastLive.current = { site: { id: c.target.id, name: c.target.name, label: c.target.label }, draft_id: liveHit.item.draft_id, title: liveHit.item.title };
        const line = alreadyLiveLine(liveHit.item.title, c.target.name);
        patch({ phase: 'none', items: [], line });
        await say(line);
        return;
      }
      if (!hit) {
        const miss = titleNotFoundLine(title, c.target.name);
        patch({ phase: 'none', items: [], line: miss });
        await say(miss);
        return;
      }
      c = { ...c, items: [hit.item] };
    }
    // Action named in the command -> ONE read-back (action + site + count); else ask.
    const named = pendingMode.current;
    const rb = named ? batchReadbackLine(named, c.items, c.target.name, c.publish.available) : null;
    let line = rb ? rb.line : batchProposalLine(c.items.length, c.target.name, c.publish.available, c.publish.reason);
    if (pendingAlreadyWritten.current) {
      // "write <queued title>": the draft exists; never a second paid write -- offer to publish it.
      pendingAlreadyWritten.current = false;
      line = (c.items.length === 1 ? 'That one’s already written and ready. ' : 'Those are already written and ready. ') + line;
    }
    // Re-push guard: a read-back that names an on-site WP draft IS the update question.
    const updateAsk = rb && c.items.some((i) => i.state === 'on_site_as_draft') ? { mode: rb.mode, draftIds: c.items.map((i) => i.draft_id) } : null;
    patch({
      phase: 'asking', items: c.items, site: { id: c.target.id, name: c.target.name, label: c.target.label },
      publishAvailable: c.publish.available, publishMessage: c.publish.message, line, updateAsk,
    });
    turnRef.current.setBatchPublish(c.target.id, c.items.map((i) => i.draft_id), channel, rb ? rb.mode : null);
    await say(line);
    if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') {
      await listenRef.current?.();
    }
  }, [deps, patch, say, turnRef, listenRef, refuse]); // eslint-disable-line react-hooks/exhaustive-deps

  const begin = useCallback(async (ask: BatchPublishAsk, channel: BatchChannel) => {
    if (!(args.canManagePublishing ? args.canManagePublishing() : false)) {
      await refuse(channel); // C6b: no site lookup, no candidates call, no cards
      return;
    }
    pendingLiveUpdate.current = false;
    afterConfirmRef.current = null;
    pendingAlreadyWritten.current = Boolean(ask.alreadyWritten);
    if (ask.updateLive) {
      if (!deps.proposeUpdateLive || !deps.executeUpdateLive) {
        // No update-live wiring in this build -> the clear refusal, no server call.
        set({ ...emptyBatchView(channel), phase: 'none', line: BATCH_UPDATE_LIVE_UNSUPPORTED });
        await say(BATCH_UPDATE_LIVE_UNSUPPORTED);
        return;
      }
      if (!ask.titleHint) {
        // "update it on the live site": only meaningful right after an "already live" line.
        const ctx = lastLive.current;
        if (!ctx) {
          set({ ...emptyBatchView(channel), phase: 'none', line: BATCH_UPDATE_LIVE_WHICH });
          await say(BATCH_UPDATE_LIVE_WHICH);
          return;
        }
        set({ ...emptyBatchView(channel), site: ctx.site, line: '' });
        await proposeLive(ctx.site, { draft_id: ctx.draft_id, title: ctx.title }, channel);
        return;
      }
      pendingLiveUpdate.current = true; // resolve the site, then match the title among already_live
    }
    // F3: a named title looks through the next BATCH_MAX_COUNT ready drafts.
    pendingTitle.current = ask.titleHint ?? null;
    pendingCount.current = pendingTitle.current ? BATCH_MAX_COUNT : ask.count;
    pendingMode.current = ask.namedMode;
    set({ ...emptyBatchView(channel), line: 'Finding your connected sites…' });
    let sites: BatchSiteOption[];
    try {
      sites = await deps.listSites();
    } catch (e) {
      patch({ phase: 'error', error: errText(e), line: BATCH_NO_SITE });
      await say(BATCH_NO_SITE);
      return;
    }
    const active: BatchActiveDomain | number | null = args.activeDomain
      ? args.activeDomain()
      : args.activeProjectId ? args.activeProjectId() : null;
    const res = resolveBatchSite(sites, ask.siteHint, active);
    if (res.kind === 'out_of_domain') {
      patch({ phase: 'none', line: BATCH_OUT_OF_DOMAIN });
      await say(BATCH_OUT_OF_DOMAIN);
      return;
    }
    if (res.kind === 'none') {
      patch({ phase: 'none', line: BATCH_NO_SITE });
      await say(BATCH_NO_SITE);
      return;
    }
    if (res.kind === 'choose') {
      patch({ phase: 'choose_site', sites: res.options, line: BATCH_PICK_SITE });
      await say(BATCH_PICK_SITE);
      return;
    }
    await loadCandidates(res.site, channel);
  }, [deps, set, patch, say, loadCandidates, args, refuse]); // eslint-disable-line react-hooks/exhaustive-deps

  const chooseSite = useCallback(async (siteId: number) => {
    const v = viewRef.current;
    const site = v?.sites.find((s) => s.id === siteId);
    if (!v || !site) return;
    await loadCandidates(site, v.channel === 'voice' ? 'voice' : v.channel);
  }, [loadCandidates]);

  const presentDrafts = useCallback(async (site: BatchSiteOption, draftIds: number[], channel: BatchChannel, opts?: { mode?: BatchMode | null; afterConfirm?: () => Promise<void> }) => {
    if (draftIds.length === 0) return;
    pendingCount.current = draftIds.length;
    // conversational-actions: a hand-off never inherits an earlier ask's title / live / mode.
    pendingTitle.current = null;
    pendingLiveUpdate.current = false;
    pendingAlreadyWritten.current = false;
    pendingMode.current = opts?.mode ?? null;
    afterConfirmRef.current = opts?.afterConfirm ?? null;
    set({ ...emptyBatchView(channel), line: 'Loading your new drafts…' });
    await loadCandidates(site, channel, draftIds);
  }, [set, loadCandidates]);

  // readBack: the mode Aura's own read-back already spoke and the user said yes to.
  // The server read-back (token-bound, from propose) is then SHOWN, and spoken only
  // if it differs (e.g. publishing switched off since) — one sentence, not two.
  const confirmInner = useCallback(async (mode: BatchMode, draftIds: number[] | undefined, channel: BatchChannel, readBack: BatchMode | null) => {
    const v = viewRef.current;
    if (!v || !v.site || v.phase === 'working') return;
    const ids = draftIds && draftIds.length > 0 ? draftIds : pendingDraftIds(v);
    if (ids.length === 0) return;
    turnRef.current.clearSubStateIfKind('batch_publish');
    patch({ phase: 'working', error: '', readback: '', resultLine: '', updateAsk: null });
    try {
      const { result } = await deps.confirm(v.site.id, ids, mode, channel, async (p) => {
        patch({ readback: p.spoken_readback });
        if (!(readBack && p.effective_mode === readBack)) {
          await say(p.spoken_readback); // the server's read-back: site + count + effective mode
        }
      });
      const cur = viewRef.current ?? v;
      const results = mergeBatchResults(cur, result.items);
      const remaining = cur.items.filter((c) => !results[c.draft_id]).length;
      patch({ phase: remaining > 0 ? 'asking' : 'done', results, resultLine: result.spoken_result });
      await say(result.spoken_result);
      // Fable IVG C1: the writes picked together with these publishes run ONLY after a fully
      // successful execute (every picked draft published / saved); a partial result writes nothing.
      const after = afterConfirmRef.current;
      afterConfirmRef.current = null;
      const allOk = ids.every((id) => results[id] && results[id].status !== 'failed');
      if (after && allOk) await after();
      else if (after) await say(BATCH_WRITES_SKIPPED);
    } catch (e) {
      // Fable IVG C1: a failed publish never runs the queued writes.
      afterConfirmRef.current = null;
      if (isPublishFeatureForbidden(e)) { await refuse(v.channel); return; }
      const msg = errText(e);
      patch({ phase: 'error', error: msg });
      await say(msg);
    }
  }, [deps, patch, say, turnRef, refuse]);

  const confirm = useCallback(async (mode: BatchMode, draftIds?: number[], channel: BatchChannel = 'tap') => {
    const v = viewRef.current;
    if (v && v.site && v.phase !== 'working') {
      // Re-push guard on the TAP path (owner decision 12-10-2026 15:29 UTC; IVG A-BA3): a tap
      // that would overwrite a WP draft already on the site asks "update it?" first.
      const t = tapUpdateAsk(v, mode, draftIds);
      if (t) {
        turnRef.current.setBatchPublish(v.site.id, t.ask.draftIds, channel, t.ask.mode);
        patch({ line: t.line, updateAsk: t.ask, ...supersedeLiveConfirm() });
        await say(t.line);
        return;
      }
      // The open update question answered by a tap: confirm exactly what it named.
      const ids = draftIds && draftIds.length > 0 ? draftIds : pendingDraftIds(v);
      const open = v.updateAsk ?? null;
      if (open && sameUpdateAsk(open, { mode: open.mode, draftIds: ids.filter((id) => !v.results[id]) })) {
        await confirmInner(open.mode, open.draftIds, channel, open.mode);
        return;
      }
    }
    await confirmInner(mode, draftIds, channel, null);
  }, [confirmInner, patch, say, turnRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAnswer = useCallback(async (answer: string, channel: BatchChannel) => {
    const ctx = turnRef.current.subState;
    if (!ctx || ctx.kind !== 'batch_publish') return;
    const step = resolveBatchPublishAnswer(matchBatchPublishAnswer(answer), ctx.reAskCount, ctx.readbackMode ?? null);
    if (ctx.readbackMode === 'update_live') {
      if (step.kind === 'execute-update-live') { await confirmUpdateLive(channel); return; }
      if (step.kind === 're-ask') {
        turnRef.current.incReask();
        patch({ line: BATCH_UPDATE_LIVE_REASK });
        await say(BATCH_UPDATE_LIVE_REASK);
        if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
        return;
      }
      await cancelUpdateLive();
      return;
    }
    if (step.kind === 'execute-update-live') return; // unreachable: only an update_live read-back yields it
    // conversational-actions (owner GO 12-10-2026 20:30 UTC): with several cards open, an answer
    // that names some of them ("2", "1 and 3", "the kambo one") narrows the choice; an ambiguous
    // title gets ONE clarifier naming the two candidates. A named mode then reads back (action +
    // site + count) for a pure yes; no mode -> "Publish or save as draft?". Nothing executes here.
    {
      const v = viewRef.current;
      const open = (v?.items ?? []).filter((i) => ctx.draftIds.includes(i.draft_id) && !v?.results[i.draft_id]);
      if (v && v.site && open.length > 1) {
        const listed = open.map((i, k) => ({ n: k + 1, title: i.title, keyword: i.focus_keyword }));
        const sel = parseListSelection(answer, listed);
        if (sel.kind === 'ambiguous' && ctx.reAskCount < 2) {
          turnRef.current.incReask();
          const line = clarifyLine(sel.candidates.map((n) => listed[n - 1]));
          patch({ line });
          await say(line);
          if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
          return;
        }
        if (sel.kind === 'some' && sel.ns.length < open.length) {
          const chosen = sel.ns.map((n) => open[n - 1]);
          const ids = chosen.map((i) => i.draft_id);
          const named = step.kind === 'execute' ? step.mode : (ctx.readbackMode === 'publish' || ctx.readbackMode === 'draft' ? ctx.readbackMode : null);
          if (named) {
            const rb = batchReadbackLine(named, chosen, v.site.name, v.publishAvailable);
            const updateAsk = chosen.some((i) => i.state === 'on_site_as_draft') ? { mode: rb.mode, draftIds: ids } : null;
            turnRef.current.setBatchPublish(v.site.id, ids, channel, rb.mode);
            patch({ line: rb.line, updateAsk, ...supersedeLiveConfirm() });
            await say(rb.line);
          } else {
            const line = batchSubsetQuestion(chosen);
            turnRef.current.setBatchPublish(v.site.id, ids, channel, null);
            patch({ line });
            await say(line);
          }
          if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
          return;
        }
      }
    }
    if (step.kind === 'execute' && !ctx.readbackMode) {
      // Re-push guard: a mode answer to the open question would UPDATE a card that is already
      // on the site as a WP draft -> ask first ("Already pushed as a draft — update it?").
      const v = viewRef.current;
      const chosen = (v?.items ?? []).filter((i) => ctx.draftIds.includes(i.draft_id));
      if (v && v.site && chosen.some((i) => i.state === 'on_site_as_draft')) {
        const rb = batchReadbackLine((step as { mode: BatchMode }).mode, chosen, v.site.name, v.publishAvailable);
        turnRef.current.setBatchPublish(v.site.id, ctx.draftIds, channel, rb.mode);
        patch({ line: rb.line, updateAsk: { mode: rb.mode, draftIds: chosen.map((i) => i.draft_id) }, ...supersedeLiveConfirm() });
        await say(rb.line);
        if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
        return;
      }
    }
    if (step.kind === 'execute') {
      const readBack = ctx.readbackMode && step.mode === ctx.readbackMode ? ctx.readbackMode : null;
      await confirmInner(step.mode, ctx.draftIds, channel, readBack);
      return;
    }
    if (step.kind === 'ask-mode' || step.kind === 're-ask') {
      turnRef.current.incReask();
      const line = step.kind === 'ask-mode' ? BATCH_ASK_MODE : ctx.readbackMode ? batchConfirmReask(ctx.readbackMode) : BATCH_REASK;
      patch({ line });
      await say(line);
      if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
      return;
    }
    turnRef.current.clearSubStateIfKind('batch_publish');
    afterConfirmRef.current = null; // Fable IVG C2: a "no" drops the writes queued behind this read-back
    patch({ phase: 'cancelled', line: BATCH_CANCEL_ACK });
    await say(BATCH_CANCEL_ACK);
  }, [confirmInner, patch, say, turnRef, listenRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const undo = useCallback(async (publicationId: number, draftId: number) => {
    const v = viewRef.current;
    if (!v) return;
    patch({ phase: 'working', error: '' });
    try {
      const res = await deps.revert(publicationId);
      // The spoken line follows the SERVER result (owner GO 22-10-2026 "fix BookedCentral too",
      // sweep #5): only ok:true is a revert; changed:false means the post was already a draft.
      if (!res || res.ok !== true) {
        patch({ phase: 'error', error: BATCH_REVERT_FAILED });
        await say(BATCH_REVERT_FAILED);
        return;
      }
      const line = res.changed === false ? BATCH_REVERT_ALREADY_DRAFT : BATCH_REVERTED;
      const cur = viewRef.current ?? v;
      const r = cur.results[draftId];
      patch({ phase: 'done', results: { ...cur.results, [draftId]: { ...r, reverted: true, undo_available: false } }, resultLine: line });
      await say(line);
    } catch (e) {
      if (isPublishFeatureForbidden(e)) { await refuse(v.channel); return; }
      const msg = errText(e);
      patch({ phase: 'error', error: msg });
      await say(msg);
    }
  }, [deps, patch, say, refuse]);

  // ---- connector 1.3.0: update a LIVE post -----------------------------------
  // Owner GO 12-10-2026 15:53 UTC (option A, verbatim: "Add a proper 'update live post'
  // action to the connector, with a saved copy for undo. That's more work, and the post
  // stays live throughout."). The SERVER read-back names the live post; only a pure yes
  // (voice/chat) or a tap confirms; the token is held in memory for that one execute.
  const liveView = (p: Partial<BatchLiveUpdateView>) => {
    const cur = viewRef.current?.liveUpdate;
    return { draftId: 0, title: '', phase: 'proposing', readback: '', resultLine: '', batchId: null, undoAvailable: false, ...(cur ?? {}), ...p } as BatchLiveUpdateView;
  };

  // release/fix5 integration (Fable IVG C-BF4, connector/live-update verdict 12-10-2026 16:52 UTC):
  // the fix4 WP-draft "update it?" ask and the live-post update confirm are never open at once.
  // Opening the WP-draft ask drops an open live confirm (its single-use token too); opening the
  // live confirm (proposeLive) clears the WP-draft ask. The turn sub-state already follows the
  // last read-back, so one pure yes answers exactly ONE of them.
  function supersedeLiveConfirm(): Partial<BatchPublishView> {
    if (viewRef.current?.liveUpdate?.phase !== 'confirm') return {};
    liveProposal.current = null;
    return { liveUpdate: liveView({ phase: 'cancelled' }) };
  }

  async function proposeLive(site: { id: number; name: string; label: string }, item: { draft_id: number; title: string }, channel: BatchChannel): Promise<void> {
    if (!deps.proposeUpdateLive) return;
    liveProposal.current = null;
    patch({ phase: 'working', error: '', updateAsk: null, liveUpdate: liveView({ draftId: item.draft_id, title: item.title, phase: 'proposing', readback: '', resultLine: '', batchId: null, undoAvailable: false }) });
    let p: UpdateLiveProposal;
    try {
      p = await deps.proposeUpdateLive(site.id, item.draft_id, channel);
    } catch (e) {
      if (isPublishFeatureForbidden(e)) { await refuse(channel); return; }
      const line = errCode(e) === BATCH_CONNECTOR_UPDATE_REQUIRED_CODE ? BATCH_CONNECTOR_UPDATE_REQUIRED : errText(e);
      patch({ phase: 'none', line, liveUpdate: liveView({ phase: 'failed', resultLine: line }) });
      await say(line);
      return;
    }
    liveProposal.current = p;
    patch({ phase: 'asking', line: p.spoken_readback, liveUpdate: liveView({ phase: 'confirm', readback: p.spoken_readback }) });
    turnRef.current.setBatchPublish(site.id, [item.draft_id], channel, 'update_live');
    await say(p.spoken_readback);
    if (channel === 'voice' && turnRef.current.subState?.kind === 'batch_publish') await listenRef.current?.();
  }

  async function confirmUpdateLive(channel: BatchChannel = 'tap'): Promise<void> {
    const p = liveProposal.current;
    const v = viewRef.current;
    if (!p || !v || !deps.executeUpdateLive || v.liveUpdate?.phase !== 'confirm') return;
    liveProposal.current = null; // single use
    turnRef.current.clearSubStateIfKind('batch_publish');
    patch({ phase: 'working', error: '', liveUpdate: liveView({ phase: 'working' }) });
    void channel;
    try {
      const r = await deps.executeUpdateLive(p);
      const ok = r.status === 'updated';
      patch({ phase: 'done', resultLine: r.spoken_result, liveUpdate: liveView({ phase: ok ? 'done' : 'failed', resultLine: r.spoken_result, batchId: ok ? r.batch_id : null, undoAvailable: ok && r.undo_available }) });
      await say(r.spoken_result);
    } catch (e) {
      if (isPublishFeatureForbidden(e)) { await refuse(v.channel); return; }
      const msg = errText(e);
      patch({ phase: 'error', error: msg, liveUpdate: liveView({ phase: 'failed', resultLine: msg }) });
      await say(msg);
    }
  }

  async function cancelUpdateLive(): Promise<void> {
    afterConfirmRef.current = null;
    liveProposal.current = null;
    turnRef.current.clearSubStateIfKind('batch_publish');
    if (!viewRef.current) return;
    patch({ phase: 'cancelled', line: BATCH_CANCEL_ACK, liveUpdate: liveView({ phase: 'cancelled' }) });
    await say(BATCH_CANCEL_ACK);
  }

  async function undoLive(): Promise<void> {
    const v = viewRef.current;
    const batchId = v?.liveUpdate?.batchId ?? null;
    if (!v || batchId == null || !deps.undoUpdateLive || !v.liveUpdate?.undoAvailable) return;
    patch({ phase: 'working', error: '' });
    try {
      const r = await deps.undoUpdateLive(batchId);
      patch({ phase: 'done', resultLine: r.spoken_result, liveUpdate: liveView({ phase: 'undone', resultLine: r.spoken_result, undoAvailable: false }) });
      await say(r.spoken_result);
    } catch (e) {
      if (isPublishFeatureForbidden(e)) { await refuse(v.channel); return; }
      const msg = errText(e);
      patch({ phase: 'error', error: msg });
      await say(msg);
    }
  }

  const updateLive = useCallback(async (draftId: number) => {
    const v = viewRef.current;
    const item = (v?.alreadyLive ?? []).find((a) => a.draft_id === draftId);
    if (!v || !v.site || !item) return;
    if (!(args.canManagePublishing ? args.canManagePublishing() : false)) { await refuse('tap'); return; }
    if (!deps.proposeUpdateLive || !deps.executeUpdateLive) { patch({ line: BATCH_UPDATE_LIVE_UNSUPPORTED }); await say(BATCH_UPDATE_LIVE_UNSUPPORTED); return; }
    await proposeLive(v.site, item, 'tap');
  }, [args, deps, patch, say, refuse]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismiss = useCallback(() => {
    liveProposal.current = null;
    afterConfirmRef.current = null;
    turnRef.current.clearSubStateIfKind('batch_publish');
    set(null);
  }, [set, turnRef]);

  const isAwaitingAnswer = useCallback(() => turnRef.current.subState?.kind === 'batch_publish', [turnRef]);
  /** Fable IVG C2: Stop / turn close / any new ask drop the writes queued behind an open read-back. */
  const dropQueuedWrites = useCallback(() => { afterConfirmRef.current = null; }, []);

  return {
    view, begin, handleAnswer, confirm, chooseSite, presentDrafts, undo, dismiss, isAwaitingAnswer, dropQueuedWrites,
    updateLive,
    confirmUpdateLive: (channel?: BatchChannel) => confirmUpdateLive(channel ?? 'tap'),
    cancelUpdateLive,
    undoUpdateLive: undoLive,
  };
}
